// FILE: src/utils/fetchWithAuth.js
// Authenticated fetch. Besides JSON handling it turns the server's session
// verdicts into app state (plan WP-2 task 10):
//   401 COURSE_2FA_REQUIRED       → identity challenge dialog; request waits, replays after
//   401/423 ACCOUNT_LOCKED …      → support dialog; request rejected when it closes
//   401 SESSION_IDLE / _REPLACED … → signed out, /login explains why
//   403 COURSE_EXPIRED            → the enrolment-ended page (still signed in, so
//                                   support stays reachable; MTO 6.3)
// Rejections carry the server body as JSON in `error.message` so callers can
// read `code` / `attemptsRemaining` without parsing prose.
import { store } from "../store";
import {
  prompt as prompt2FA,
  __pendingRequestMap as pendingMap,
  rejectPendingChallenges,
  clear as clear2FA
} from "../features/course2faSlice";
import { LOCK_CODES, needSupport, suspendForSupport } from "../features/supportPromptSlice";
import { sessionEnded, SESSION_END_CODES } from "../features/authSlice";
import { apiUrl } from "../services/api";
import { enrolmentExpired } from "../features/timeSlice";

function errorFor(payload, fallback) {
  if (payload && typeof payload === "object" && payload.code) {
    const err = new Error(Object.keys(payload).length > 1 ? JSON.stringify(payload) : payload.code);
    err.code = payload.code;
    err.body = payload;
    return err;
  }
  return new Error(fallback);
}

export default function fetchWithAuth(url, options = {}) {
  const finalUrl = apiUrl(url);

  if (!(options.body instanceof FormData)) {
    options.headers = { "Content-Type": "application/json", ...(options.headers || {}) };
  }
  options.credentials = "include";

  return new Promise(async (resolve, reject) => {
    try {
      const res = await fetch(finalUrl, options);

      if (res.status === 401 || res.status === 423) {
        let payload = {};
        try {
          payload = await res.json();
        } catch {
          /* empty body */
        }

        if (payload?.code === "COURSE_2FA_REQUIRED") {
          const requestKey = Date.now().toString(36) + Math.random().toString(36);
          pendingMap.set(requestKey, { url: finalUrl, options, resolve, reject });
          store.dispatch(prompt2FA({ requestKey, challenge: payload.challenge }));
          return; // keep the promise pending
        }

        if (LOCK_CODES.includes(payload?.code)) {
          // A lock also ends any open identity challenge and the session.
          rejectPendingChallenges("ACCOUNT_LOCKED");
          store.dispatch(clear2FA());
          suspendForSupport(reject);
          store.dispatch(needSupport({ code: payload.code, support: payload.support }));
          return;
        }

        if (SESSION_END_CODES.includes(payload?.code) && store.getState().auth?.user) {
          rejectPendingChallenges(payload.code);
          store.dispatch(sessionEnded({ code: payload.code }));
        }
        return reject(errorFor(payload, "Unauthorized"));
      }

      if (!res.ok) {
        const text = await res.text();
        let payload = null;
        try {
          payload = JSON.parse(text);
        } catch {
          /* not JSON */
        }
        if (res.status === 403 && payload?.code === "COURSE_EXPIRED" && store.getState().auth?.user) {
          store.dispatch(enrolmentExpired());
        }
        return reject(payload?.code ? errorFor(payload) : new Error(text || "Request failed"));
      }

      const json = await res.json();
      resolve(json);
    } catch (err) {
      reject(err);
    }
  });
}
